import getTimesheetsStatus from '@sdk/timesheets/helpers/getTimesheetsStatus';
import extendUserModel from './extendUserModel';
import { extendTimesheetEntry, extendTimesheetProjection } from './extendTimesheetModel';
import calculateDateParameters from '../calculateDateParameters';
import getShiftDisplayDuration from './getShiftDisplayDuration';
import getShiftDisplayLabels from './getShiftDisplayLabels';
import getShiftDurationMinutes from './getShiftDurationMinutes';
import canActOnOffer from './canActOnOffer';
import shouldDisplayLocation from './shouldDisplayLocation';
import getExchangeData from './getExchangeData';
import utils from '../utils';
import context from '../context';
import { SHIFT_BLOCK_SOURCES, TIMESHEET_ENTRY_TYPES } from '../constants';
import getShiftTags from './getShiftTags';
import { isShiftPendingAcceptance } from '../helpers/shiftAcceptance/shiftAcceptanceUtils';
import gettext from '../lang/gettext';

export default function extendShiftModel(
  model,
  { mutate = false, nowDate, calendarDisplayOptimize = false } = {}
) {
  const result = mutate ? model : Object.assign({}, model);

  if (typeof result.dtstart === 'string') {
    Object.assign(result, calculateDateParameters(result));

    result.getDtstartString = function getDtstartString(date = result.dtstart) {
      return `${utils.formatDate(date)}T${utils.formatTime(date, false, true)}:00${
        result.dtstartTimezone || ''
      }`;
    };

    result.getDtendString = function getDtendString(date = result.dtend) {
      return `${utils.formatDate(date)}T${utils.formatTime(date, false, true)}:00${
        result.dtendTimezone || ''
      }`;
    };
  }

  if (result.user) {
    result.user = extendUserModel(result.user, {
      mutate,
      calendarDisplayOptimize,
    });
  }

  result.date = utils.formatDate(result.dtstart);

  const now = nowDate || utils.getNowDate();
  result.isPast = result.dtendLocal < now;
  result.hasStarted = result.dtstartLocal < now;

  if (!('duration' in result)) {
    result.duration = (result.dtend - result.dtstart) / 1000 / 60 / 60;
  }

  result.scheduledStartTime = result.duration ? utils.normalizeTime(result.dtstart) : null;
  result.scheduledEndTime = result.duration ? utils.normalizeTime(result.dtend) : null;

  result.displayDuration = getShiftDisplayDuration(result);
  result.paidDurationMinutes = getShiftDurationMinutes(result);
  result.openEnd = 'openEnd' in result ? result.openEnd : result.duration === 0;
  result.sort = 1;
  result.key = result.id;

  if (result.location && context.personasDict[result.location.id]) {
    result.location = context.personasDict[result.location.id];
  }

  if (result.position && context.personasDict[result.position.id]) {
    result.position = context.personasDict[result.position.id];
  }

  if (result.location) {
    const isShiftAcceptanceEnabled =
      context.shiftAcceptanceEnabled &&
      context.isOrgFeatureEnabled &&
      context.isOrgFeatureEnabled('shiftAcceptance');
    const canAcceptShift =
      isShiftAcceptanceEnabled && isShiftPendingAcceptance(result) && result.user.isMe;

    result.canBeSelected = context.me.canActOnLocation(result.location) || canAcceptShift;
  }

  if (context.isOrgFeatureEnabled && context.isOrgFeatureEnabled('tags')) {
    result.tags = getShiftTags(result, context.tags);
  }

  const { label, labelHiddenEndTime, breakLabel, tagLabel } = getShiftDisplayLabels(result);
  result.displayLabel = label;
  result.tagsLabel = tagLabel;

  if (result.displayDuration) {
    result.displayLabel += ` • ${result.displayDuration}`;
  }
  result.breakLabel = breakLabel;

  result.monthBlockSecondRow = shouldDisplayLocation(context)
    ? `${result.position.name} • ${result.location.name}`
    : result.position.name;
  result.auxDisplayLabel = result.monthBlockSecondRow;

  const employeeDisplayedName = result.user ? result.user.fullname : gettext('No employee');
  result.monthBlockThirdRow = employeeDisplayedName;

  const appearanceMap = {
    [SHIFT_BLOCK_SOURCES.TIME]: label,
    [SHIFT_BLOCK_SOURCES.DURATION]: result.displayDuration,
    [SHIFT_BLOCK_SOURCES.ASSIGNEE]: employeeDisplayedName,
    [SHIFT_BLOCK_SOURCES.POSITION]: result.position.name,
    [SHIFT_BLOCK_SOURCES.LOCATION]: result.location.name,
    [SHIFT_BLOCK_SOURCES.TAG]: tagLabel,
  };

  const appearanceMapWithHiddenEndTimes = {
    [SHIFT_BLOCK_SOURCES.TIME]: labelHiddenEndTime,
    [SHIFT_BLOCK_SOURCES.DURATION]: null,
    [SHIFT_BLOCK_SOURCES.ASSIGNEE]: employeeDisplayedName,
    [SHIFT_BLOCK_SOURCES.POSITION]: result.position.name,
    [SHIFT_BLOCK_SOURCES.LOCATION]: result.location.name,
    [SHIFT_BLOCK_SOURCES.TAG]: tagLabel,
  };

  setShiftAppearance(result, appearanceMap);
  setShiftAppearance(result, appearanceMapWithHiddenEndTimes, true);

  if (result.offer && result.offer.user) {
    result.offer.user = extendUserModel(result.offer.user);
  } else {
    delete result.offer;
  }

  if (result.applications) {
    result.applications.forEach((application) => {
      application.user = extendUserModel(application.user);
    });

    result.myApplication = result.applications.find((application) => application.user.isMe) || null;
  } else {
    result.applications = [];
    result.myApplication = null;
  }

  if (result.rrule) {
    result.recurrenceIntervalDescription = getIntervalMode(result.rrule.interval);

    const days = {
      SU: gettext('Sunday'),
      MO: gettext('Monday'),
      TU: gettext('Tuesday'),
      WE: gettext('Wednesday'),
      TH: gettext('Thursday'),
      FR: gettext('Friday'),
      SA: gettext('Saturday'),
    };

    result.recurrenceDaysDescription = result.rrule.byday
      ? result.rrule.byday.split(',').map((d) => days[d])
      : [];

    if (result.recurrenceDaysDescription.length === 7) {
      result.recurrenceDaysDescription = gettext('Every day');
    } else {
      result.recurrenceDaysDescription = result.recurrenceDaysDescription.join(' • ');
    }
  } else {
    result.recurrenceIntervalDescription = null;
    result.recurrenceDaysDescription = null;
  }

  if (result.rrule) {
    if (typeof result.rrule.until === 'string') {
      result.rrule.untilDate = utils.getRealDate(result.rrule.until.substr(0, 19));
    } else if (result.rrule.until === null) {
      result.rrule.untilDate = null;
    }

    if (typeof result.rrule.from === 'string') {
      result.rrule.fromDate = utils.getRealDate(result.rrule.from.substr(0, 19));
    }
  }

  result.timesheets = result.timesheets || [];
  result.timesheets = result.timesheets.map((originalTimesheet) => {
    const timesheet = { ...originalTimesheet };

    if (typeof timesheet.clockIn === 'string') {
      timesheet.clockInLocal = new Date(timesheet.clockIn);
      timesheet.clockIn = utils.getRealDate(timesheet.clockIn);
    }

    if (timesheet.clockOut && typeof timesheet.clockOut === 'string') {
      timesheet.clockOutLocal = new Date(timesheet.clockOut);
      timesheet.clockOut = utils.getRealDate(timesheet.clockOut);
    }

    timesheet.user = extendUserModel(timesheet.user);
    return timesheet;
  });

  result.timesheetEntries = (result.timesheetEntries || []).map(extendTimesheetEntry);

  if ('timesheetEntriesBefore' in result) {
    result.timesheetEntriesBefore = result.timesheetEntriesBefore.map(extendTimesheetEntry);
  }

  result.timesheetProjections = (result.timesheetProjections || []).map(extendTimesheetProjection);
  result.timesheetStatus = getTimesheetsStatus(result.timesheetProjections);
  result.timesheetStatusChangedBy = getTimesheetStatusChangedBy(result.timesheetProjections);

  const firstProjection =
    (result.timesheetProjections.length && result.timesheetProjections[0]) || null;
  const lastProjection =
    (result.timesheetProjections.length &&
      result.timesheetProjections[result.timesheetProjections.length - 1]) ||
    null;

  result.clockInStatus = {
    get clockedIn() {
      return lastProjection && !lastProjection.clockOut;
    },
    get eligibleForBreak() {
      return Boolean(context.laborCostSettings && context.laborCostSettings.shiftBreakType);
    },
    get onBreak() {
      return lastProjection && lastProjection.onBreak;
    },
    get clockedOut() {
      return lastProjection && Boolean(lastProjection.clockOut);
    },
  };

  result.actualBreakDuration = result.timesheetProjections.reduce(
    (acc, projection) => acc + projection.breakMinutes,
    0
  );

  delete result.clockedBackIn;
  delete result.clockedIn;
  delete result.clockedInLocal;
  delete result.clockInTime;
  delete result.clockedOut;
  delete result.clockedOutLocal;
  delete result.clockOutTime;
  delete result.actualDuration;
  delete result.actualDifference;

  if (
    result.timesheetProjections.length > 1 ||
    (lastProjection && lastProjection.lastAction.getTime() !== lastProjection.clockIn.getTime())
  ) {
    const lastEndBreakeEntry =
      result.timesheetEntries.length &&
      result.timesheetEntries.findLast((entry) => entry.type === TIMESHEET_ENTRY_TYPES.BREAK_END);

    if (lastEndBreakeEntry) {
      result.clockedBackIn =
        lastEndBreakeEntry.timestamp.getTime() > lastProjection.clockIn.getTime()
          ? lastEndBreakeEntry.timestamp
          : lastProjection.clockIn;
    } else {
      result.clockedBackIn = lastProjection.clockIn;
    }
  }

  if (firstProjection && result.user && firstProjection.user.id === result.user.id) {
    result.clockedIn = firstProjection.clockIn;
    result.clockedInLocal = firstProjection.clockInLocal;
    result.clockInTime = utils.dateFromHours(utils.formatTime(result.clockedIn, false, true));
  }

  if (
    lastProjection &&
    result.user &&
    lastProjection.user.id === result.user.id &&
    lastProjection.clockOut
  ) {
    result.clockedOut = lastProjection.clockOut;
    result.clockedOutLocal = lastProjection.clockOutLocal;

    if (result.clockedOut) {
      result.clockOutTime = utils.dateFromHours(utils.formatTime(result.clockedOut, false, true));
      result.actualDuration = result.timesheetProjections.reduce(
        (acc, projection) => acc + projection.paidMinutes,
        0
      );
      result.actualDifference = result.actualDuration - result.paidDurationMinutes;
    }
  }

  result.canApplyForShift =
    context.me.isManager ||
    (result.user && result.user.isMe) ||
    (context.me.locationIds.includes(result.location.id) &&
      context.me.positionIds.includes(result.position.id));
  result.showAvailableShiftIcon = result.available && !result.isPast && result.canApplyForShift;
  result.showOfferedShiftIcon = result.offer && canActOnOffer(context, result);
  result.showTasksIcon = Boolean(result.taskTemplates?.filter(({ deleted }) => !deleted).length);

  if (!result.exchangeData) {
    result.exchangeData = getExchangeData(result, context.me);
  }

  result.showBigIcon =
    result.showAvailableShiftIcon || result.showOfferedShiftIcon || result.exchangeData.isSwap;
  result.isExtended = true;

  return result;
}

function getTimesheetStatusChangedBy(projections) {
  const actorIds = Object.keys(
    projections.reduce((acc, p) => {
      if (p.statusChangedBy) {
        acc[p.statusChangedBy.id] = true;
      }

      return acc;
    }, {})
  );

  if (actorIds.length === 1) {
    return extendUserModel({ id: actorIds[0] });
  }

  return null;
}

function setShiftAppearance(result, appearanceMap, withHiddenEndTimes) {
  const prop = withHiddenEndTimes ? 'shiftAppearanceHiddenEndTime' : 'shiftAppearance';
  result[prop] = {};
  if (context.shiftAppearance) {
    Object.entries(context.shiftAppearance).forEach(([mode, lines]) => {
      result[prop][mode] = Object.entries(lines).reduce((acc, [line, sources]) => {
        // trim unbreakable white sapces if there is more than 1 item
        const filterSourcesFunc = (s) => (sources.length > 1 ? s && s.trim() : s);

        acc[line] = sources
          .map((s) => appearanceMap[s])
          .filter(Boolean)
          .filter(filterSourcesFunc)
          .join(' • ');
        const timeSourceIndex = sources.findIndex((s) => s === SHIFT_BLOCK_SOURCES.TIME);
        if (timeSourceIndex >= 0) {
          acc.timeSource = [line, timeSourceIndex];
        }
        return acc;
      }, {});
    });
  }
}

function getIntervalMode(interval) {
  if (interval === 0) {
    return gettext('never');
  } else if (interval === 1) {
    return gettext('every week');
  }

  return gettext('every %(count)s weeks', { count: interval });
}
